
import React from 'react';
import { X, Database, CheckCircle2, AlertCircle, Info, Table } from 'lucide-react';

interface DbHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: 'dark' | 'light';
}

const DbHelpModal: React.FC<DbHelpModalProps> = ({ isOpen, onClose, theme }) => {
  if (!isOpen) return null;

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300",
    container: `${theme === 'dark' ? 'bg-[#161616] border-[#262626]' : 'bg-white border-slate-200'} border w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
    code: theme === 'dark' ? 'bg-black/40 text-indigo-400' : 'bg-slate-100 text-indigo-600',
  };

  const columns = [
    "IdTransaccion", "Ticket", "Simbolo", "Precio", "Cantidad", "Total", 
    "Fecha", "TicketUSDT", "Status", "Usuario_Cartera", "Categoria"
  ];

  return (
    <div className={themeClasses.overlay} onClick={onClose}>
      <div className={themeClasses.container} onClick={e => e.stopPropagation()}>
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-600 rounded-xl">
                <Database size={24} className="text-white" />
              </div>
              <h2 className={`text-xl font-black uppercase tracking-tight ${themeClasses.textMain}`}>Estructura Cloud Database</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <X size={20} className={themeClasses.textMain} />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <p className={`${themeClasses.textMuted} text-sm mb-4`}>
                Los datos se sincronizan directamente con Supabase. Los registros en la tabla <code className="font-bold">APP_Transacciones</code> deben seguir este esquema:
              </p>
              <div className={`p-4 rounded-xl font-mono text-[10px] grid grid-cols-2 gap-2 ${themeClasses.code}`}>
                {columns.map((col, i) => (
                  <div key={col} className="flex items-center space-x-1">
                    <span className="opacity-40">{i+1}.</span>
                    <span className="font-bold">{col}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
                <div className="flex items-start space-x-3">
                    <CheckCircle2 size={16} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                    <div>
                        <span className={`block text-xs font-black uppercase ${themeClasses.textMain}`}>Sincronización en Tiempo Real</span>
                        <span className={`${themeClasses.textMuted} text-[11px]`}>
                          Cualquier cambio guardado desde la App se refleja inmediatamente en Supabase.
                        </span>
                    </div>
                </div>
                <div className="flex items-start space-x-3">
                    <Table size={16} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                    <div>
                        <span className={`block text-xs font-black uppercase ${themeClasses.textMain}`}>Campos de Clasificación</span>
                        <span className={`${themeClasses.textMuted} text-[11px]`}>
                          <code className="font-bold">Status:</code> Compra / Venta. <br/>
                          <code className="font-bold">Categoria:</code> criptomoneda / acciones.
                        </span>
                    </div>
                </div>
            </div>

            <div className={`mt-4 p-4 rounded-xl border border-indigo-500/20 bg-indigo-500/5`}>
                <div className="flex items-center space-x-2 mb-2 text-indigo-500">
                    <Info size={14} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Nota sobre Seguridad</span>
                </div>
                <p className="font-mono text-[9px] text-slate-500 leading-relaxed">
                  Las credenciales de acceso se gestionan en la tabla Login. Se requiere el correo de usuario exacto para la verificación 2FA.
                </p>
            </div>
          </div>

          <button 
            onClick={onClose}
            className="w-full mt-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-indigo-600/20"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default DbHelpModal;
